/**
 * SystemAdmin Permissions — 1:1 mirror of
 * `Inspect.Domain.Constants.Permissions.SystemAdmin.*` (backend).
 *
 * THIS FILE IS THE SINGLE FRONTEND SOURCE OF TRUTH for admin-side permission
 * strings. Consumers MUST import constants from here — never hard-code the
 * string. Backend handlers in `Inspect.Admin.Application` call
 * `EnsureAdminPermission(Permissions.SystemAdmin.X.Y)` against the same
 * literals.
 *
 * Security contract:
 *   - The backend is authoritative. `AdminBaseHandler.EnsureAdminPermission`
 *     re-checks the caller's permission on every request.
 *   - The constants here exist only for UX (route guards, sidebar filtering,
 *     action-button gates) so the admin UI doesn't render controls that
 *     would 403 on click.
 *
 * When updating:
 *   1. Keep literals exactly aligned with the backend `SystemAdmin.*`
 *      namespace. Mismatches silently fail-closed (gate never passes).
 *   2. Preserve `as const` so TypeScript infers literal-string types.
 *
 * Backend source:
 *   inspect-backend/src/Inspect.Domain/Constants/Permissions.cs (under the
 *   SystemAdmin region).
 */

export const Permissions = {
	// Client (tenant) management — view, create, deactivate, impersonate
	Clients: {
		ViewAll: "system:clients:view:all",
		View: "system:clients:view",
		Create: "system:clients:create",
		Update: "system:clients:update",
		Delete: "system:clients:delete",
		Activate: "system:clients:activate",
		Deactivate: "system:clients:deactivate",
		Impersonate: "system:clients:impersonate",
	},

	// Client-user management (managing users within any client)
	ClientUsers: {
		ViewAll: "system:clientusers:view:all",
		View: "system:clientusers:view",
		Create: "system:clientusers:create",
		Update: "system:clientusers:update",
		Delete: "system:clientusers:delete",
		ResetPassword: "system:clientusers:resetpassword",
		ManageRoles: "system:clientusers:manageroles",
	},

	// Admin-user management (managing other InspectTec admin users)
	AdminUsers: {
		ViewAll: "system:adminusers:view:all",
		Create: "system:adminusers:create",
		Update: "system:adminusers:update",
		Delete: "system:adminusers:delete",
		ManageRoles: "system:adminusers:manageroles",
	},

	// System configuration + global resources
	System: {
		ViewSettings: "system:settings:view",
		ManageSettings: "system:settings:manage",
		ViewLogs: "system:logs:view",
		ViewAuditTrail: "system:audit:view",
		ManagePermissions: "system:permissions:manage",
		ManageRoles: "system:roles:manage",
	},

	// Admin dashboard + analytics
	Dashboard: {
		ViewOverview: "system:dashboard:overview",
		ViewMetrics: "system:dashboard:metrics",
		ViewClientHealth: "system:dashboard:clienthealth",
		ExportReports: "system:dashboard:export",
	},

	// Support operations (read/modify client data + view inspections/issues)
	Support: {
		ViewClientData: "system:support:viewdata",
		ModifyClientData: "system:support:modifydata",
		ViewInspections: "system:support:inspections",
		ViewIssues: "system:support:issues",
		// Support Center tickets (inbox, replies, assignment, settings)
		ViewTickets: "system:support:tickets:view",
		ManageTickets: "system:support:tickets:manage",
	},

	// Agency management (existing — pre-Subscription-module)
	Agencies: {
		ViewAll: "system:agencies:view:all",
		View: "system:agencies:view",
		Create: "system:agencies:create",
		Update: "system:agencies:update",
		Delete: "system:agencies:delete",
		ManageClients: "system:agencies:manageclients",
	},

	// Subscription module — Associations (3rd-party payer entities)
	Associations: {
		Manage: "system:associations:manage",
	},

	// Subscription module — BillingAccounts (polymorphic payer layer)
	BillingAccounts: {
		Manage: "system:billingaccounts:manage",
	},

	// Subscription module — Subscriptions (tier changes, blocks, trial mgmt)
	Subscriptions: {
		Manage: "system:subscriptions:manage",
	},

	// Subscription module — Invoices (issue, record payment, void)
	Invoices: {
		Manage: "system:invoices:manage",
	},

	// Subscription module — combined-quota overage report
	QuotaReports: {
		View: "system:quotareports:view",
	},

	// Subscription module — Plan + PlanVersion catalog (read-only)
	Plans: {
		View: "system:plans:view",
	},

	// System Course Library — cross-tenant catalog of training courses curated
	// by InspectTec staff and imported by client tenants.
	CourseLibrary: {
		Manage: "system:courselibrary:manage",
		Publish: "system:courselibrary:publish",
		ManageTerms: "system:courselibrary:terms",
	},

	// System Checklist Library — versioned catalog of inspection checklists
	// published from the InspectTec Library tenant and copied down into
	// client tenants (industry starter packs + per-tenant grants).
	ChecklistLibrary: {
		Manage: "system:checklistlibrary:manage",
		Publish: "system:checklistlibrary:publish",
	},

	// Inspection LayoutMode catalog + per-tenant entitlement. Granting /
	// revoking SystemLayoutMode access for a Client; toggling the catalog
	// IsActive kill switch. Mirrors backend `SystemAdmin.LayoutModes.Manage`.
	LayoutModes: {
		Manage: "system:layoutmodes:manage",
	},
} as const;

/* -------------------------------------------------------------------------- */
/* Type helpers                                                               */
/* -------------------------------------------------------------------------- */

type LeafStrings<T> = T extends string
	? T
	: T extends Record<string, unknown>
		? { [K in keyof T]: LeafStrings<T[K]> }[keyof T]
		: never;

/** Union of every admin-side permission string this app knows about. */
export type Permission = LeafStrings<typeof Permissions>;
